import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Select, Typography } from '@arco-design/web-react';

import {
  DEFAULT_MAAS_OPTIONS,
  MaasRuntimeType,
  MaasWorkloadType,
  normalizeMaasOptions,
  normalizeMaasRuntimeType,
  normalizeMaasWorkloadType,
} from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { useResourceSectionReadOnly } from '@/delivery-type-plugin/components';
import { isFieldInteractionLocked } from '@/delivery-type-plugin/platform/capability-registry';

import { RUNTIME_CONFIG_FIELD_KEYS } from './fieldKeys';
import { resolveRuntimeConfigPaths } from './resolvePaths';
import type { RuntimeConfigBlockProps } from './types';

const FormItem = Form.Item;

type DeployModeSelectValue = 'normal' | 'pd';

/** 表单存 boolean，Select 使用 normal | pd */
function DeployModeSelect({ value, onChange, disabled }: { value?: boolean; onChange?: (value: boolean) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const options = useMemo(
    () => [
      { label: t('data-center:maas.deploy.normal'), value: 'normal' as const },
      { label: t('data-center:maas.inference.pdSeparation'), value: 'pd' as const },
    ],
    [t],
  );
  const selectValue: DeployModeSelectValue | undefined = value === true ? 'pd' : value === false ? 'normal' : undefined;

  return (
    <Select
      options={options}
      disabled={disabled}
      value={selectValue}
      placeholder={t('components:please.select')}
      onChange={(next: DeployModeSelectValue) => onChange?.(next === 'pd')}
    />
  );
}

/** 运行配置能力块：运行时类型（+ workload）与可选部署模式下拉 */
export function RuntimeConfigBlock({ form, options, paths: rawPaths, fieldModes, blockMode }: RuntimeConfigBlockProps) {
  const { t } = useTranslation();
  const sectionReadOnly = useResourceSectionReadOnly();
  const paths = useMemo(() => ({ ...resolveRuntimeConfigPaths(''), ...(rawPaths || {}) }), [rawPaths]);
  const normalizedOptions = useMemo(() => normalizeMaasOptions(options?.typeOptions || DEFAULT_MAAS_OPTIONS), [options?.typeOptions]);
  const runtimeTypeOnly = options?.runtimeTypeOnly === true;
  const showDeployMode = options?.showDeployMode === true;
  /** 直建编辑 / 下单固定：整块 locked；自定义下单按 fieldModes 逐字段锁 */
  const blockLocked = options?.locked === true || options?.isEdit === true;
  const control = { fieldModes, blockMode };
  const runtimeTypeLocked = blockLocked || sectionReadOnly || isFieldInteractionLocked(RUNTIME_CONFIG_FIELD_KEYS.runtimeType, control);
  const deployModeLocked = blockLocked || sectionReadOnly || isFieldInteractionLocked(RUNTIME_CONFIG_FIELD_KEYS.deployMode, control);
  const runtimeType = Form.useWatch('runtime.type', form) as MaasRuntimeType | undefined;
  const normalizedRuntimeType = normalizeMaasRuntimeType(runtimeType) || runtimeType;
  const pdSeparation = Form.useWatch(paths.deployMode, form) === true;

  const runtimeTypeOptions = useMemo(
    () =>
      Object.keys(normalizedOptions.runtime || {}).map((key) => {
        const value = normalizeMaasRuntimeType(key) as MaasRuntimeType;
        return {
          label: t(`data-center:maas.runtime.${value.toLowerCase()}`, value),
          value,
        };
      }),
    [normalizedOptions.runtime, t],
  );

  const workloadOptions = useMemo(() => {
    if (!normalizedRuntimeType) return [];
    return (normalizedOptions.runtime?.[normalizedRuntimeType] || []).map((w) => {
      const value = normalizeMaasWorkloadType(w) as MaasWorkloadType;
      return {
        label: t(`data-center:maas.workload.${value.toLowerCase()}`, value),
        value,
      };
    });
  }, [normalizedRuntimeType, normalizedOptions.runtime, t]);

  const handleRuntimeTypeChange = (type: MaasRuntimeType) => {
    const normalizedType = normalizeMaasRuntimeType(type) || type;
    form.setFieldValue('runtime.type', normalizedType);
    form.setFieldValue('runtime.workload', undefined);
    if (runtimeTypeOnly) return;
    const workloads = normalizedOptions.runtime?.[normalizedType] || [];
    if (workloads.length === 1) {
      form.setFieldValue('runtime.workload', normalizeMaasWorkloadType(workloads[0]) || workloads[0]);
    }
  };

  useEffect(() => {
    if (!runtimeType) return;
    const normalizedType = normalizeMaasRuntimeType(runtimeType);
    if (normalizedType && normalizedType !== runtimeType) {
      form.setFieldValue('runtime.type', normalizedType);
    }
    const current = form.getFieldValue('runtime.workload');
    if (runtimeTypeOnly) {
      if (current) form.setFieldValue('runtime.workload', undefined);
      return;
    }
    const normalizedWorkload = normalizeMaasWorkloadType(current);
    if (current && normalizedWorkload && normalizedWorkload !== current) {
      form.setFieldValue('runtime.workload', normalizedWorkload);
    }
    if (normalizedWorkload) return;
    const activeType = normalizedType || runtimeType;
    const workloads = normalizedOptions.runtime?.[activeType] || [];
    if (workloads.length === 1) {
      form.setFieldValue('runtime.workload', normalizeMaasWorkloadType(workloads[0]) || workloads[0]);
    }
  }, [form, normalizedOptions.runtime, runtimeType, runtimeTypeOnly]);

  useEffect(() => {
    if (!showDeployMode) return;
    if (form.getFieldValue(paths.deployMode) === undefined) {
      form.setFieldValue(paths.deployMode, false);
    }
  }, [form, paths.deployMode, showDeployMode]);

  const onDeployModeChange = options?.onDeployModeChange;
  useEffect(() => {
    if (!showDeployMode) return;
    onDeployModeChange?.(pdSeparation);
  }, [onDeployModeChange, pdSeparation, showDeployMode]);

  const deployModeLabel = t('data-center:maas.deploy.mode');
  const deployModeRequiredRules = [
    {
      validator(value: boolean | undefined, callback: (error?: string) => void) {
        if (value === true || value === false) {
          callback();
          return;
        }
        callback(t('common:validate.required.select', { name: deployModeLabel }));
      },
    },
  ];

  return (
    <>
      <FormItem
        label={t('data-center:maas.runtime.type')}
        field="runtime.type"
        rules={[{ required: true, message: t('common:required.field') }]}
      >
        <Select
          options={runtimeTypeOptions}
          onChange={handleRuntimeTypeChange}
          placeholder={t('components:please.select')}
          disabled={runtimeTypeLocked}
        />
      </FormItem>
      {!runtimeTypeOnly ? (
        <FormItem field="runtime.workload" hidden rules={[{ required: true, message: t('common:required.field') }]}>
          <Select options={workloadOptions} />
        </FormItem>
      ) : null}
      {showDeployMode ? (
        <FormItem
          label={deployModeLabel}
          required
          extra={
            pdSeparation && options?.showDeployModeHint !== false ? (
              <Typography.Text type="secondary">{t('data-center:maas.inference.pdSeparation.hint')}</Typography.Text>
            ) : undefined
          }
        >
          <FormItem field={paths.deployMode} initialValue={false} noStyle rules={deployModeRequiredRules} triggerPropName="value">
            <DeployModeSelect disabled={deployModeLocked} />
          </FormItem>
        </FormItem>
      ) : null}
    </>
  );
}
